import { create } from 'zustand';
import { LUT_MIME, MAX_FONT_BYTES, MAX_LUT_BYTES, extOf, fontMimeForExt, lutMimeForExt, mediaMimeForExt, parseCube, uuidv7 } from '@vidmakr/shared';
import { api } from './api';
import { isValidFont, registerLocalFont } from './fonts';
import { reducers } from './stdb';
import { errorMessage, toast } from './toast';

export interface UploadItem {
  assetId: string;
  name: string;
  size: number;
  loaded: number;
  status: 'uploading' | 'finishing' | 'done' | 'error' | 'canceled';
  error?: string;
  startedAt: number;
}

interface UploadState {
  items: Record<string, UploadItem>;
  set(id: string, patch: Partial<UploadItem>): void;
  clearFinished(): void;
}

export const useUploads = create<UploadState>((set) => ({
  items: {},
  set(id, patch) {
    set((s) => ({ items: { ...s.items, [id]: { ...s.items[id], ...patch } } }));
  },
  clearFinished() {
    set((s) => ({
      items: Object.fromEntries(Object.entries(s.items).filter(([, v]) => v.status === 'uploading' || v.status === 'finishing')),
    }));
  },
}));

/**
 * A file that is not a browser `File`, read lazily in byte ranges (e.g. a file
 * on an external drive, read through the desktop app). Large files never have
 * to be in memory at once: each upload part is read when it is sent.
 */
export interface UploadSource {
  name: string;
  size: number;
  /** MIME type if known ('' otherwise; the extension decides). */
  type: string;
  slice(start: number, end: number): Promise<Blob>;
}

/** What uploadFiles accepts. */
export type UploadInput = File | UploadSource;

/** The whole file as a Blob (Files already are; sources read once). */
async function wholeBlob(file: UploadInput): Promise<Blob> {
  return file instanceof Blob ? file : file.slice(0, file.size);
}

const controllers = new Map<string, AbortController>();
const ACCEPT = /^(video|audio|image|font)\/|^application\/x-cube-lut$/;

/** File picker filter for everything uploadFiles accepts. */
export const UPLOAD_ACCEPT = 'video/*,audio/*,image/*,.ttf,.otf,.woff,.woff2,.cube';
const PARALLEL_PARTS = 4;

function mimeFor(file: UploadInput): string {
  const ext = extOf(file.name);
  // Browsers report fonts under assorted legacy types, or none at all.
  const font = fontMimeForExt(ext);
  if (font) return font;
  const lut = lutMimeForExt(ext);
  if (lut) return lut;
  if (file.type) return file.type;
  return mediaMimeForExt(ext) ?? 'application/octet-stream';
}

function putWithProgress(url: string, body: Blob, headers: Record<string, string>, onProgress: (n: number) => void, signal: AbortSignal) {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve(xhr.getResponseHeader('ETag') ?? '') : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort());
    xhr.send(body);
  });
}

async function withRetry<T>(fn: () => Promise<T>, signal: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (signal.aborted || attempt >= 4) throw e;
      await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
    }
  }
}

/** Checks a file before it is uploaded (toasts why not); a font's data is returned to use right away. */
async function checkFile(file: UploadInput, mime: string): Promise<{ ok: boolean; fontData: ArrayBuffer | null }> {
  const no = { ok: false, fontData: null };
  if (!ACCEPT.test(mime)) {
    toast.error(`${file.name}: unsupported file type`);
    return no;
  }
  if (mime === LUT_MIME) {
    if (file.size > MAX_LUT_BYTES) {
      toast.error(`${file.name}: LUT files can be at most 20 MB`);
      return no;
    }
    try {
      parseCube(await (await wholeBlob(file)).text());
    } catch (e) {
      toast.error(`${file.name}: ${(e as Error).message}`);
      return no;
    }
  }
  let fontData: ArrayBuffer | null = null;
  if (mime.startsWith('font/')) {
    if (file.size > MAX_FONT_BYTES) {
      toast.error(`${file.name}: font files can be at most 30 MB`);
      return no;
    }
    fontData = await (await wholeBlob(file)).arrayBuffer();
    if (!(await isValidFont(fontData))) {
      toast.error(`${file.name}: not a font file this browser can read`);
      return no;
    }
  }
  return { ok: true, fontData };
}

/** Uploads files into an org (optionally scoped to a project/folder). */
export async function uploadFiles(files: UploadInput[], target: { orgId: string; projectId?: string; folderId?: string; spaceId?: string }): Promise<string[]> {
  const ids: string[] = [];
  for (const file of files) {
    const mime = mimeFor(file);
    const { ok, fontData } = await checkFile(file, mime);
    if (!ok) continue;
    const assetId = uuidv7();
    ids.push(assetId);
    // Usable in the preview right away, before the upload finishes.
    if (fontData) void registerLocalFont(assetId, fontData);
    void uploadOne(file, assetId, {
      register: () =>
        reducers().createAsset({
          id: assetId,
          orgId: target.orgId,
          projectId: target.projectId ?? '',
          folderId: target.folderId ?? '',
          name: file.name,
          mime,
          ext: extOf(file.name),
          sizeBytes: file.size,
          spaceId: target.spaceId ?? '',
        }),
      query: '',
      cancel: () => reducers().cancelUpload({ id: assetId }),
    });
  }
  return ids;
}

/**
 * Replaces an asset's file with another one, keeping the asset: every clip
 * using it, its review links and its place in the library stay. The asset
 * switches to the new file once that is uploaded and processed.
 */
export async function replaceFile(assetId: string, file: UploadInput): Promise<boolean> {
  const mime = mimeFor(file);
  if (!(await checkFile(file, mime)).ok) return false;
  try {
    await reducers().replaceAsset({ id: assetId, name: file.name, mime, ext: extOf(file.name), sizeBytes: file.size });
  } catch (e) {
    toast.error(`${file.name}: ${errorMessage(e)}`);
    return false;
  }
  void uploadOne(file, assetId, {
    register: async () => {},
    query: '?replace=1',
    cancel: () => reducers().cancelReplaceAsset({ id: assetId }),
  });
  return true;
}

/** How an upload starts and stops: a new file, or a replacement of an asset's file. */
interface UploadKind {
  /** Registers the upload with the module (reserves quota). */
  register(): Promise<unknown>;
  /** Added to the upload API's URLs. */
  query: string;
  /** Called when the upload fails or is canceled. */
  cancel(): Promise<unknown>;
}

async function uploadOne(file: UploadInput, assetId: string, kind: UploadKind) {
  const store = useUploads.getState();
  store.set(assetId, { assetId, name: file.name, size: file.size, loaded: 0, status: 'uploading', startedAt: Date.now() });
  const ac = new AbortController();
  controllers.set(assetId, ac);
  let uploadToken: string | undefined;
  try {
    await kind.register();
    const start = await api<any>(`/api/uploads/${assetId}/start${kind.query}`, { method: 'POST' });
    if (start.mode === 'single') {
      const body = await wholeBlob(file);
      await withRetry(() => putWithProgress(start.url, body, start.headers ?? {}, (n) => store.set(assetId, { loaded: n }), ac.signal), ac.signal);
      store.set(assetId, { status: 'finishing', loaded: file.size });
      await api(`/api/uploads/${assetId}/complete${kind.query}`, { method: 'POST', json: {} });
    } else {
      uploadToken = start.uploadToken as string;
      const partSize: number = start.partSize;
      const count: number = start.partCount;
      const loadedByPart = new Map<number, number>();
      const report = () => store.set(assetId, { loaded: [...loadedByPart.values()].reduce((a, b) => a + b, 0) });
      const parts: { partNumber: number; etag: string }[] = [];
      let next = 1;
      let urls: Record<number, string> = {};
      const fetchUrls = async (from: number) => {
        const numbers = Array.from({ length: Math.min(40, count - from + 1) }, (_, i) => from + i);
        const res = await api<{ urls: Record<number, string> }>(`/api/uploads/${assetId}/parts${kind.query}`, { method: 'POST', json: { uploadToken, partNumbers: numbers } });
        urls = { ...urls, ...res.urls };
      };
      const workerFn = async () => {
        while (next <= count) {
          const n = next++;
          if (!urls[n]) await fetchUrls(n);
          const blob = await file.slice((n - 1) * partSize, Math.min(file.size, n * partSize));
          const etag = await withRetry(
            () => putWithProgress(urls[n], blob, {}, (l) => (loadedByPart.set(n, l), report()), ac.signal),
            ac.signal,
          );
          if (!etag) throw new Error('Storage did not return an ETag (check bucket CORS ExposeHeaders)');
          parts.push({ partNumber: n, etag });
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL_PARTS, count) }, workerFn));
      store.set(assetId, { status: 'finishing', loaded: file.size });
      await api(`/api/uploads/${assetId}/complete${kind.query}`, { method: 'POST', json: { uploadToken, parts } });
    }
    store.set(assetId, { status: 'done' });
  } catch (e) {
    const canceled = ac.signal.aborted;
    store.set(assetId, { status: canceled ? 'canceled' : 'error', error: errorMessage(e) });
    if (!canceled) toast.error(`${file.name}: ${errorMessage(e)}`);
    await api(`/api/uploads/${assetId}/abort${kind.query}`, { method: 'POST', json: { uploadToken } }).catch(() => {});
    await kind.cancel().catch(() => {});
  } finally {
    controllers.delete(assetId);
  }
}

export function cancelUpload(assetId: string) {
  controllers.get(assetId)?.abort();
}

// Warn before leaving with uploads in flight.
window.addEventListener('beforeunload', (e) => {
  const busy = Object.values(useUploads.getState().items).some((i) => i.status === 'uploading' || i.status === 'finishing');
  if (busy) {
    e.preventDefault();
    e.returnValue = '';
  }
});
